import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import SecureFormItem from "@/common/SecureFormItem";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { getOptionsFromRefList, isNonEmptyVariable } from "@/util/common_function";
import { useState, useImperativeHandle, forwardRef, useEffect, useRef } from "react";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import { MODULES_LIST } from "@/util/common_variable";
import { SCHEMAS } from "@/constants/schemas";

// ── Numeric-only input helpers ──────────────────────────────────────────────
const onlyDigitsKeyDown = (e) => {
  const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
  if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};
const stripNonDigits = (value) => value.replace(/\D/g, "");
// ────────────────────────────────────────────────────────────────────────────

// ── Validation rules ────────────────────────────────────────────────────────
const VALIDATION_RULES = {
  charge: { required: true, label: "Charge" },
};

const validate = (form, requireOriginalCharge) => {
  const errors = {};
  Object.entries(VALIDATION_RULES).forEach(([field, rule]) => {
    if (rule.required && !isNonEmptyVariable(form[field])) {
      errors[field] = `${rule.label} is required`;
    }
  });
  if (requireOriginalCharge?.toLowerCase() === "true" && !isNonEmptyVariable(form.originalCharge)) {
    errors.originalCharge = "Original Charge is required";
  }
  return errors;
};
// ────────────────────────────────────────────────────────────────────────────

const OffenseWarrantChargeDetailForm = forwardRef(({ initialValues = null, startingPoint = "" }, ref) => {
  const { activeWarrant, bookingsFieldsEnabled } = useActiveWarrant();
  // Derive the refListItemGlobalID from the saved charge label so the
  // first-render fetch works correctly on reopen (chargeID is not in the API payload).
  const deriveChargeID = (chargeLabel) => {
    if (!chargeLabel) return null;
    const options = getOptionsFromRefList("Charges", false, false, true);
    return options.find((o) => o.label === chargeLabel)?.value ?? null;
  };

  const [chargeID, setChargeID] = useState(() => deriveChargeID(initialValues?.charge));
  const [originalChargeID, setOriginalChargeID] = useState(() => deriveChargeID(initialValues?.originalCharge));
  const [requireOriginalCharge, setRequireOriginalCharge] = useState("");
  const isFirstRender = useRef(true);

  const [form, setForm] = useState({
    warrantChargeID:      initialValues?.warrantChargeID      ?? null,
    warrantID:            initialValues?.warrantID            ?? activeWarrant?.warrantId ?? null,
    chargeNumber:         initialValues?.chargeNumber         ?? "",
    charge:               initialValues?.charge               ?? null,
    offenseCode:          initialValues?.offenseCode          ?? "",
    chargeLevelID:        initialValues?.chargeLevelID        ?? null,
    chargeCount:          initialValues?.chargeCount          ?? "",
    chargeModifierID:     initialValues?.chargeModifierID     ?? null,
    ordinance:            initialValues?.ordinance            ?? "",
    oca:                  initialValues?.oca                  ?? "",
    additionalChargeInfo: initialValues?.additionalChargeInfo ?? "",
    comments:             initialValues?.comments             ?? "",
    controllingCharge: initialValues !== null ? (initialValues?.controllingCharge ?? false) : true,
    sealed:               initialValues?.sealed               ?? false,
    dateAdded:            initialValues?.dateAdded            ?? null,
    timeAdded:            initialValues?.timeAdded            ?? null,
    creatorStaffID:       initialValues?.creatorStaffID       ?? null,
    originalCharge:       initialValues?.originalCharge       ?? null,
    originalOffenseCode:  initialValues?.originalOffenseCode  ?? null,
    checkBank:            initialValues?.checkBank            ?? null,
    checkNumber:          initialValues?.checkNumber          ?? null,
    checkAmount:          initialValues?.checkAmount          ?? null,
    checkPayableTo:       initialValues?.checkPayableTo       ?? null,
    checkReturnReason:    initialValues?.checkReturnReason    ?? null,
    importedCodeSection:  initialValues?.importedCodeSection  ?? null,
    importedStatuteTitle: initialValues?.importedStatuteTitle ?? null,
  });

  const [errors, setErrors] = useState({});

  const patch = (fields) => {
    setForm((prev) => ({ ...prev, ...fields }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(fields).forEach((key) => { delete next[key]; });
      return next;
    });
  };

  // ── Main charge lookup ───────────────────────────────────────────────────
  useEffect(() => {
    const isFirst = isFirstRender.current;
    if (isFirst) isFirstRender.current = false;

    if (!isNonEmptyVariable(chargeID)) {
      if (!isFirst) {
        patch({ chargeLevelID: null, offenseCode: "" });
        setRequireOriginalCharge("");
      }
      return;
    }

    let cancelled = false;

    api
      .get(`${API_ENDPOINTS.vwCharges_refListItemGlobalID}/${chargeID}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.length) {
          const attribs = data[0].refListItemAttribs ?? [];
          const requireOrig = attribs.find((r) => r.attribName === "RequireOriginalCharge")?.attribValue;

          setRequireOriginalCharge(requireOrig ?? "");

          // On first render (reopening a saved record), form fields are already
          // hydrated from initialValues — only restore requireOriginalCharge, skip patching.
          if (isFirst) return;

          const chargeLevel = data[0].chargeLevel;
          const chargeLevelOptions = getOptionsFromRefList("Charge_Level", false, false, true);
          const matched = chargeLevelOptions.find(
            (o) => o.label?.toLowerCase() === chargeLevel?.toLowerCase()
          );

          const ucr = attribs.find((r) => r.attribName === "UOC_Code")?.attribValue;
          const ofl = attribs.find((r) => r.attribName === "OFL")?.attribValue;

          patch({
            chargeLevelID:        matched?.value ?? null,
            offenseCode:          isNonEmptyVariable(ucr) ? ucr : "",
            additionalChargeInfo: isNonEmptyVariable(ofl) ? ofl : "",
            // Clear original charge fields when main charge changes
            originalCharge:      null,
            originalOffenseCode: null,
          });
          setOriginalChargeID(null);
        }
      })
      .catch((err) => console.error("Failed to fetch charge details:", err));

    return () => { cancelled = true; };
  }, [chargeID]);

  // ── Original charge lookup ───────────────────────────────────────────────
  useEffect(() => {
    if (!isNonEmptyVariable(originalChargeID)) {
      patch({ originalOffenseCode: null });
      return;
    }

    let cancelled = false;

    api
      .get(`${API_ENDPOINTS.vwCharges_refListItemGlobalID}/${originalChargeID}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.length) {
          const ucr = data[0].refListItemAttribs?.find(
            (r) => r.attribName === "UOC_Code"
          )?.attribValue;

          patch({ originalOffenseCode: isNonEmptyVariable(ucr) ? ucr : "" });
        }
      })
      .catch((err) => console.error("Failed to fetch original charge details:", err));

    return () => { cancelled = true; };
  }, [originalChargeID]);

  // Expose getFormData() to parent via ref
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      const validationErrors = validate(form, requireOriginalCharge);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return null;
      }
      return form;
    },
  }));

  const fieldProps = (fieldName, extra = {}) => ({
    ...extra,
    validateStatus: errors[fieldName] ? "error" : "",
    help:           errors[fieldName] ?? "",
  });

  return (
    <FieldSecurityProvider
      entity={SCHEMAS.WARRANT_CHARGES.SCHEMA_NAME}
      startingPoint={MODULES_LIST.WARRANTS}
      bookingsFieldsEnabled={bookingsFieldsEnabled}
    >
      <StyledSectionHeading marginBottom="0px">
        <span>Offense Information</span>
      </StyledSectionHeading>

      {/* ── Checkboxes row ── */}
      <StyledGridRow gutter={16} justify="end">
        <StyledGridCol>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.CONTROLLING_CHARGE}
            formItemProps={
              { 
                label: "Controlling Charge?",
                layout: "horizontal",
                labelPostion: "label-right" 
              }
            }
          >
            <Checkbox
              checked={form.controllingCharge}
              onChange={(e) => patch({ controllingCharge: e.target.checked })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.SEALED}
            formItemProps={
              {
                label: "Sealed?",
                layout: "horizontal",
                labelPostion: "label-right"
              }
            }
          >
            <Checkbox
              checked={form.sealed}
              onChange={(e) => patch({ sealed: e.target.checked })}
            />
          </SecureFormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* ── Charge # ── */}
      <StyledGridRow gutter={16}>
        <StyledGridCol>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.CHARGE_NUMBER}
            formItemProps={fieldProps("chargeNumber", { label: "Charge #" })}
          >
            <TextField
              value={form.chargeNumber}
              onKeyDown={onlyDigitsKeyDown}
              onChange={(e) => patch({ chargeNumber: stripNonDigits(e.target.value) })}
              className="field--pink"
            />
          </SecureFormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* ── Main fields ── */}
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={12}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.CHARGE}
            formItemProps={fieldProps("charge", {
              label: "Charge",
              required: true,
              rules: [{ required: true, message: "Charge is required" }],
            })}
          >
            <Select
              className="address-select--pink"
              value={form.charge}
              options={getOptionsFromRefList("Charges", false, false, true)}
              placeholder="Charge"
              showSearch={true}
              variant="outlined"
              onChange={(val, option) => {
                patch({ charge: option?.label, chargeLevelID: null, offenseCode: "", chargeCount: 1 });
                setChargeID(val);
              }}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={4}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.OFFENSE_CODE}
            overrideDisabled
            formItemProps={{ label: "Offense Code" }}
          >
            <TextField
              value={form.offenseCode}
              onChange={(e) => patch({ offenseCode: e.target.value })}
              className="field--pink"
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={4}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.CHARGE_LEVEL_ID}
            formItemProps={{ label: "Level" }}
          >
            <Select
              value={form.chargeLevelID}
              options={getOptionsFromRefList("Charge_Level", false, false, true)}
              placeholder="Level"
              showSearch={true}
              variant="outlined"
              onChange={(val) => patch({ chargeLevelID: val })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={4}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.CHARGE_COUNT}
            formItemProps={{ label: "Count" }}
          >
            <TextField
              value={form.chargeCount}
              onKeyDown={onlyDigitsKeyDown}
              onChange={(e) => patch({ chargeCount: stripNonDigits(e.target.value) })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={6}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.CHARGE_MODIFIER_ID}
            formItemProps={{ label: "Charge Modifier" }}
          >
            <Select
              value={form.chargeModifierID}
              options={getOptionsFromRefList("Charge_Modifier", false, false, true)}
              placeholder="Charge Modifier"
              variant="outlined"
              onChange={(val) => patch({ chargeModifierID: val })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={6}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.ORDINANCE}
            formItemProps={{ label: "Code/Ordinance Section" }}
          >
            <TextField
              value={form.ordinance}
              onChange={(e) => patch({ ordinance: e.target.value })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={4}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.OCA}
            formItemProps={{ label: "OCA" }}
          >
            <TextField
              value={form.oca}
              onChange={(e) => patch({ oca: e.target.value })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.ADDITIONAL_CHARGE_INFO}
            formItemProps={{ label: "Additional Charge Info" }}
          >
            <TextField
              value={form.additionalChargeInfo}
              onChange={(e) => patch({ additionalChargeInfo: e.target.value })}
            />
          </SecureFormItem>
        </StyledGridCol>

        <StyledGridCol lg={24}>
          <SecureFormItem
            fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.COMMENTS}
            formItemProps={{ label: "Comments" }}
          >
            <TextField
              inputType="textarea"
              rows="3"
              value={form.comments}
              onChange={(e) => patch({ comments: e.target.value })}
            />
          </SecureFormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* ── Original Charge Information (shown only when RequireOriginalCharge = true) ── */}
      {requireOriginalCharge?.toLowerCase() === "true" && (
        <>
          <StyledSectionHeading marginBottom="0px">
            <span>Original Charge Information</span>
          </StyledSectionHeading>

          <StyledGridRow gutter={16}>
            <StyledGridCol lg={12}>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.ORIGINAL_CHARGE}
                formItemProps={fieldProps("originalCharge", {
                  label: "Original Charge",
                  required: true,
                  rules: [{ required: true, message: "Original Charge is required" }],
                })}
              >
                <Select
                  className="address-select--pink"
                  value={form.originalCharge}
                  options={getOptionsFromRefList("Charges", false, false, true)}
                  placeholder="Original Charge"
                  showSearch={true}
                  variant="outlined"
                  onChange={(val, option) => {
                    patch({ originalCharge: option?.label, originalOffenseCode: "" });
                    setOriginalChargeID(val);
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName={SCHEMAS.WARRANT_CHARGES.FIELDS.ORIGINAL_OFFENSE_CODE}
                overrideDisabled
                formItemProps={{ label: "Offense Code" }}
              >
                <TextField
                  value={form.originalOffenseCode ?? ""}
                  onChange={(e) => patch({ originalOffenseCode: e.target.value })}
                  className="field--pink"
                />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>
        </>
      )}
    </FieldSecurityProvider>
  );
});

OffenseWarrantChargeDetailForm.displayName = "OffenseWarrantChargeDetailForm";

export default OffenseWarrantChargeDetailForm;
